// What the device page shows, worked out from its two sources, so the page itself only lays it out.
//
//   the system info result  (the script get-systeminfo): the computer, how old it is, whether it should be replaced and why.
//   the software of the machine (cma/softwaredevice): every program it reported with what the policy says about it.

export const SYSINFO_SCRIPT = 'get-systeminfo';

const list = (v) => (Array.isArray(v) ? v : []);
const has = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
const text = (v) => String(v).trim();
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);

// [{label, value}] without the ones the machine did not report.
const facts = (pairs) => pairs.filter(([, value]) => has(value)).map(([label, value]) => ({ label, value: Array.isArray(value) ? value.join(', ') : text(value) }));

export const computerFacts = (r) => {
  const x = r || {};
  const os = [x.OperatingSystem, x.WindowsDisplayVersion].filter(has).join(' ');
  const domain = has(x.Domain) ? `${x.Domain}${x.PartOfDomain === true ? ' (joined)' : x.PartOfDomain === false ? ' (not joined)' : ''}` : null;
  const memory = typeof x.MemoryInstalledGB === 'number' ? `${x.MemoryInstalledGB} GB${typeof x.MemoryUsableGB === 'number' && x.MemoryUsableGB !== x.MemoryInstalledGB ? ` (${x.MemoryUsableGB} GB usable)` : ''}` : null;
  const bios = [x.BiosName, x.BiosVersion].filter(has).join(' ');
  return facts([
    ['Operating system', os], ['Windows version', x.WindowsVersion], ['Architecture', x.Architecture], ['Installation', x.WindowsInstallationType],
    ['Last user', x.LastUser], ['Domain', domain], ['Manufacturer', x.Manufacturer], ['Model', x.Model], ['Serial number', x.SerialNumber],
    ['Processor', x.Processor], ['Cores', x.TotalPhysicalCores], ['Memory', memory], ['BIOS', bios], ['BIOS released', x.BiosReleaseDate],
    ['Antivirus', x.AntivirusProduct], ['Firewall on', yesNo(x.WindowsFirewallEnabled)], ['.NET Framework', x.DotNetVersion], ['Graphics', x.DisplayAdapters],
    ['Reported', x.ReportDate],
  ]);
};

export const VERDICTS = {
  replaceNow: { label: 'Replace now', tone: 'err' },
  planReplacement: { label: 'Plan a replacement', tone: 'warn' },
  healthy: { label: 'Healthy', tone: 'ok' },
};
export const SEVERITIES = { hard: { label: 'Replace now', tone: 'err', rank: 0 }, strong: { label: 'Plan', tone: 'warn', rank: 1 }, soft: { label: 'Tips the balance', tone: 'none', rank: 2 } };

// The age and replacement part: null when the machine has not reported it (a result from before it was added has only the computer's details).
export const replacement = (r) => {
  const a = (r || {}).ReplacementAssessment;
  if (!a || typeof a !== 'object') return null;
  const age = (r || {}).DeviceAge || {};
  const dates = a.dates || {};
  const m = a.metrics || {};
  const drives = list(m.drives).map((d) => [d.model || `Drive ${d.number}`, [d.mediaType, d.isSystem ? 'system drive' : null, d.health, typeof d.wearPercent === 'number' ? `${d.wearPercent}% worn` : null].filter(has).join(', ')]);
  return {
    verdict: VERDICTS[a.verdict] || { label: has(a.verdict) ? text(a.verdict) : 'Not assessed', tone: 'none' },
    reasons: list(a.verdictReasons).map(text),
    findings: list(a.findings).filter((f) => f && has(f.issue)).sort((p, q) => (SEVERITIES[p.severity]?.rank ?? 9) - (SEVERITIES[q.severity]?.rank ?? 9)),
    facts: facts([
      ['Chassis', a.chassis], ['Estimated age', typeof age.estimatedAgeYears === 'number' ? `${age.estimatedAgeYears} years${age.ageSource ? ` (from ${age.ageSource === 'serial' ? 'the serial number' : 'the first Windows install'})` : ''}` : null],
      ['In service since', dates.inServiceDate], ['Age limit', typeof dates.ageLimitYears === 'number' ? `${dates.ageLimitYears} years` : null], ['Replacement due', dates.replacementDueDate],
      ['Windows 11', m.windows11 ? (m.windows11.onWindows11 ? 'Running it' : m.windows11.eligible ? 'Eligible' : 'Not eligible') : null],
      ['System drive', m.systemDrive && typeof m.systemDrive.freePercent === 'number' ? `${m.systemDrive.freeGB} of ${m.systemDrive.sizeGB} GB free (${m.systemDrive.freePercent}%)` : null],
      ['Battery health', m.battery && typeof m.battery.healthPercent === 'number' ? `${m.battery.healthPercent}%` : null],
      ['Unexpected shutdowns', m.stability && typeof m.stability.unexpectedShutdowns === 'number' ? `${m.stability.unexpectedShutdowns} in ${m.stability.days} days` : null],
      ['Blue screens', m.stability && typeof m.stability.blueScreens === 'number' ? `${m.stability.blueScreens} in ${m.stability.days} days` : null],
      ['Median boot', m.boot && typeof m.boot.medianSeconds === 'number' ? `${m.boot.medianSeconds} seconds` : null],
      ...drives,
    ]),
    notRun: list((r || {}).ChecksNotRun).map((c) => `${c.check}: ${c.reason}`),
  };
};

// ---- software ------------------------------------------------------------------------------------------------------------------------

// The policy's word for a program (mode) and the state it is in (status), as the device page shows them. rank orders the states: what needs
// attention first.
export const STATUS = {
  'forbidden-present': { label: 'Forbidden', tone: 'err', rank: 0, meaning: 'The policy says this program must not be installed.' },
  missing: { label: 'Missing', tone: 'err', rank: 1, meaning: 'The policy requires this program and it is not installed.' },
  outdated: { label: 'Outdated', tone: 'warn', rank: 2, meaning: 'A newer version is available.' },
  oldcopy: { label: 'Old copy', tone: 'warn', rank: 3, meaning: 'A newer copy of this program is also installed. This one has to be removed.' },
  unverified: { label: 'Unverified', tone: 'none', rank: 4, meaning: 'There is no way to tell whether it is current.' },
  notinstalled: { label: 'Not installed', tone: 'none', rank: 5, meaning: 'A rule names this program and it is not installed.' },
  current: { label: 'Up to date', tone: 'ok', rank: 6, meaning: 'As new as the newest version we know of.' },
  notinpolicy: { label: 'Not in policy', tone: 'none', rank: 7, meaning: 'No rule names this program.' },
};
export const MODES = { required: 'Required', allowed: 'Allowed', forbidden: 'Forbidden' };
export const statusOf = (s) => STATUS[s] || { label: s || '-', tone: 'none', rank: 9, meaning: '' };

// Whether one row of the device's list can be removed from here, and if not why ('' when it can). A removal is of one exact copy, by the name
// and version the machine reported, so the row needs both; a program the policy requires is not offered, because removing it would only
// make it show as missing.
export const removeBlock = (row) => {
  if (!row?.installed) return 'It is not installed.';
  if (row.mode === 'required') return 'The policy requires this program.';
  if (!String(row.version || '').trim()) return 'The machine did not report a version, so this copy cannot be named.';
  return '';
};

export const scopeWords = (scope) => ({ machine: 'Machine-wide', user: 'For a user', 'system-profile': 'System profile' }[scope] || '');

// One row per program, alphabetical by the name it is shown under (the catalog's name where the policy knows the program).
export const programRows = (programs) => list(programs).filter((p) => p && has(p.name)).map((p) => ({
  ...p,
  shown: p.displayName || p.name,
  policy: MODES[p.mode] || 'Not in policy',
  statusLabel: statusOf(p.status).label,
  kind: p.programType === 'Microsoft' ? 'microsoft' : 'thirdparty',
})).sort((a, b) => a.shown.localeCompare(b.shown, undefined, { sensitivity: 'base', numeric: true }) || String(a.version).localeCompare(String(b.version), undefined, { numeric: true }));

// The rows that match every word typed (in the name, publisher, version or the policy's reason) and each chosen filter ('' is all).
export const filterRows = (rows, { text: typed = '', thirdParty = false } = {}) => {
  const words = typed.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((r) => (!thirdParty || r.kind !== 'microsoft')
    && words.every((w) => `${r.shown} ${r.name} ${r.publisher || ''} ${r.version || ''} ${r.reason || ''}`.toLowerCase().includes(w)));
};

// ---- policy --------------------------------------------------------------------------------------------------------------------------

// What can be done to a row of the policy list, in the order the buttons show: [{ action, why }] where `why` is the reason it cannot be run from
// here ('' when it can). Install is for a program that is not installed and may be (required and missing, or allowed); Update for one that is
// outdated; Remove for an installed copy the policy does not require (allowed or forbidden), and for an old copy of a required program.
export const ACTIONS = { install: 'Install', update: 'Update', remove: 'Uninstall' };

export const policyActions = (row) => {
  if (!row) return [];
  const noCatalog = has(row.wingetId) ? '' : 'The policy rule has no catalog entry for this program, so it cannot be installed from here.';
  const actions = [];
  if (!row.installed) {
    if (row.status === 'missing' || (row.status === 'notinstalled' && row.mode === 'allowed')) actions.push({ action: 'install', why: noCatalog });
    return actions;
  }
  if (row.status === 'outdated') actions.push({ action: 'update', why: noCatalog });
  if (row.mode !== 'required' || row.status === 'oldcopy') actions.push({ action: 'remove', why: has(row.version) ? '' : 'The machine did not report a version, so this copy cannot be named.' });
  return actions;
};

// Every program a policy rule names, installed or not, alphabetical by the name it is shown under.
export const policyRows = (programs) => programRows(programs).filter((r) => r.policy !== 'Not in policy');
